import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Form, Input, DatePicker, TimePicker, Select, message, Space, Tag, Popconfirm, Radio, Row, Col, InputNumber, Dropdown, Alert } from 'antd';
import { PlusOutlined, ClockCircleOutlined, CheckCircleOutlined, SyncOutlined, CloseCircleOutlined, EditOutlined, DeleteOutlined, UserAddOutlined, EyeOutlined, CheckOutlined, StopOutlined, MoreOutlined } from '@ant-design/icons';
import appointmentService from '../../services/appointmentService';
import { secretaryService, doctorService, settingsService, patientService } from '../../services';
import { isOnline, subscribe as onConnectivity } from '../../services/connectivity';
import SearchFilter from '../../components/SearchFilter';
import FormModal from '../../components/FormModal';
import DuplicatePatientModal from '../../components/DuplicatePatientModal';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';
import './Appointments.css';

const Appointments = () => {
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [isNewPatient, setIsNewPatient] = useState(false);
    const [defaultPaymentAmount, setDefaultPaymentAmount] = useState(0);
    // A branch secretary serves several doctors, so the appointment needs one named.
    const [bookableDoctors, setBookableDoctors] = useState([]);
    // Existing patients matching the "new patient" details being typed in.
    const [duplicates, setDuplicates] = useState([]);
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [pendingBooking, setPendingBooking] = useState(null);
    const [booking, setBooking] = useState(false);
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const handleSearch = (values) => {
        let filtered = appointments;

        if (values.patientName) {
            filtered = filtered.filter(a =>
                a.patientId.name.toLowerCase().includes(values.patientName.toLowerCase())
            );
        }

        if (values.phoneNumber) {
            filtered = filtered.filter(a =>
                a.patientId.phoneNumber.includes(values.phoneNumber)
            );
        }

        if (values.status) {
            filtered = filtered.filter(a => a.status === values.status);
        }

        setFilteredAppointments(filtered);
    };

    const handleReset = () => {
        setFilteredAppointments(appointments);
    };

    // Determine user role and base path
    const isDoctor = user?.role === 'doctor';
    const basePath = isDoctor ? '/doctor' : '/secretary';

    useEffect(() => {
        fetchTodayAppointments();
        fetchPatients();
        fetchSettings();
        fetchBookableDoctors();

        // Auto-refresh every 30 seconds - but not while offline, where every
        // tick would just fail and flash an error. Refresh straight away when
        // the connection comes back instead.
        const interval = setInterval(() => {
            if (isOnline()) fetchTodayAppointments();
        }, 30000);
        const unsubscribe = onConnectivity((online) => {
            if (online) fetchTodayAppointments();
        });
        return () => {
            clearInterval(interval);
            unsubscribe();
        };
    }, []);

    const fetchTodayAppointments = async () => {
        setLoading(true);
        try {
            const response = await appointmentService.getTodayAppointments();
            setAppointments(response.data);
            setFilteredAppointments(response.data);
        } catch (error) {
            message.error(t('failedToFetchAppointments'));
        } finally {
            setLoading(false);
        }
    };

    const fetchPatients = async () => {
        try {
            // Use the correct service based on user role
            const response = isDoctor
                ? await doctorService.getPatients()
                : await secretaryService.getPatients();
            console.log('Fetched patients:', response.data);
            setPatients(response.data || []);
        } catch (error) {
            console.error('Failed to fetch patients:', error);
            message.error(t('failedToFetchPatients'));
            setPatients([]);
        }
    };

    const fetchBookableDoctors = async () => {
        try {
            const response = await appointmentService.getBookableDoctors();
            setBookableDoctors(response.data || []);
        } catch (error) {
            console.error('Failed to fetch bookable doctors:', error);
            setBookableDoctors([]);
        }
    };

    // Checked while the new-patient fields are being filled in, so a returning
    // patient is offered before a second record is created.
    const lookForDuplicates = async () => {
        const { name, phoneNumber } = form.getFieldsValue(['name', 'phoneNumber']);
        if (!name && !phoneNumber) return;
        try {
            const res = await patientService.checkDuplicate({ name, phoneNumber });
            setDuplicates(res.data || []);
        } catch (error) {
            console.error('Duplicate check failed:', error);
        }
    };

    // Book for the patient who already exists instead of registering them twice.
    const selectExistingPatient = async (patient) => {
        setDuplicateModalOpen(false);
        setDuplicates([]);
        setIsNewPatient(false);
        // Refresh first so the dropdown has an option to show for this id.
        await fetchPatients();
        form.setFieldsValue({ patientId: patient._id });
        message.info(t('existingPatientSelected') || `Booking for the existing patient: ${patient.name}`);
    };

    const fetchSettings = async () => {
        const doctorId = isDoctor ? user._id : user.doctorId;
        // A branch secretary has no single doctor, so there is no one default fee
        // to read - asking for /settings/undefined only produced a 500.
        if (!doctorId) {
            setDefaultPaymentAmount(0);
            return;
        }
        try {
            const data = await settingsService.getSettings(doctorId);
            setDefaultPaymentAmount(data.appointmentPayAmount || 0);
        } catch (error) {
            console.error('Failed to fetch settings:', error);
            setDefaultPaymentAmount(0);
        }
    };

    const handleBookAppointment = () => {
        form.resetFields();
        setIsNewPatient(false);
        setDuplicates([]);
        form.setFieldsValue({
            paymentAmount: defaultPaymentAmount,
            // Nothing to choose when the account serves a single doctor.
            ...(bookableDoctors.length === 1 ? { doctorId: bookableDoctors[0]._id } : {})
        });
        setModalVisible(true);
    };

    const handlePatientTypeChange = (e) => {
        setIsNewPatient(e.target.value === 'new');
        if (e.target.value === 'new') {
            form.setFieldsValue({ patientId: undefined });
        }
    };

    const buildAppointmentData = (values, { force = false } = {}) => {
        const common = {
            // Only sent when the account can book for more than one doctor;
            // otherwise the server resolves it from the account itself.
            doctorId: values.doctorId,
            appointmentDate: values.appointmentDate.toDate(),
            appointmentTime: values.appointmentTime.format('hh:mm A'),
            notes: values.notes,
            paymentAmount: values.paymentAmount || 0
        };

        if (isNewPatient) {
            return {
                ...common,
                newPatient: {
                    name: values.name,
                    phoneNumber: values.phoneNumber,
                    age: values.age,
                    gender: values.gender,
                    address: values.address || '',
                    force
                }
            };
        }

        return { ...common, patientId: values.patientId };
    };

    const submitBooking = async (values, options) => {
        setBooking(true);
        try {
            await appointmentService.bookAppointment(buildAppointmentData(values, options));
            message.success(isNewPatient ? t('newPatientCreatedAndBooked') : t('appointmentBookedSuccessfully'));
            setModalVisible(false);
            setDuplicateModalOpen(false);
            setDuplicates([]);
            form.resetFields();
            setIsNewPatient(false);
            fetchTodayAppointments();
            fetchPatients(); // Refresh patient list
        } catch (error) {
            // 409 = this patient already has a file; offer it instead of a second one.
            if (error.response?.status === 409) {
                setPendingBooking(values);
                setDuplicates(error.response.data?.data || []);
                setDuplicateModalOpen(true);
            } else {
                message.error(error.response?.data?.message || t('failedToBookAppointment'));
            }
        } finally {
            setBooking(false);
        }
    };

    const handleSubmit = (values) => submitBooking(values);

    const handleCheckIn = async (appointmentId) => {
        try {
            await appointmentService.checkInAppointment(appointmentId);
            message.success(t('patientCheckedIn'));
            fetchTodayAppointments();
        } catch (error) {
            message.error(t('failedToCheckIn'));
        }
    };

    const handleNotifyDoctor = async (appointmentId, patientId) => {
        try {
            await appointmentService.notifyDoctor(appointmentId);
            message.success(t('doctorNotified'));
            fetchTodayAppointments();
        } catch (error) {
            message.error(t('failedToNotifyDoctor'));
        }
    };

    const handleCancel = async (appointmentId) => {
        try {
            await appointmentService.cancelAppointment(appointmentId);
            message.success(t('appointmentCancelled'));
            fetchTodayAppointments();
        } catch (error) {
            message.error(t('failedToCancelAppointment'));
        }
    };

    const handleMarkCompleted = async (appointmentId) => {
        try {
            await appointmentService.markAsCompleted(appointmentId);
            message.success(t('appointmentMarkedCompleted'));
            fetchTodayAppointments();
        } catch (error) {
            message.error(t('failedToMarkCompleted'));
        }
    };

    const handleMarkNoShow = async (appointmentId) => {
        try {
            await appointmentService.markAsNoShow(appointmentId);
            message.success(t('appointmentMarkedNoShow'));
            fetchTodayAppointments();
        } catch (error) {
            message.error(t('failedToMarkNoShow'));
        }
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            scheduled: { color: 'blue', icon: <ClockCircleOutlined />, text: t('scheduled') },
            arrived: { color: 'green', icon: <CheckCircleOutlined />, text: t('arrived') },
            'in-consultation': { color: 'purple', icon: <SyncOutlined spin />, text: t('inConsultation') },
            completed: { color: 'default', icon: <CheckCircleOutlined />, text: t('completed') },
            cancelled: { color: 'red', icon: <CloseCircleOutlined />, text: t('cancelled') }
        };

        const config = statusConfig[status] || statusConfig.scheduled;
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
    };

    const getWaitingTime = (checkInTime) => {
        if (!checkInTime) return '-';
        const now = new Date();
        const checkedIn = new Date(checkInTime);
        const diffMinutes = Math.floor((now - checkedIn) / 60000);
        return `${diffMinutes} min`;
    };

    const columns = [
        {
            title: t('time'),
            dataIndex: 'appointmentTime',
            key: 'appointmentTime',
            width: 100,
            sorter: (a, b) => {
                const timeA = dayjs(a.appointmentTime, 'hh:mm A');
                const timeB = dayjs(b.appointmentTime, 'hh:mm A');
                return timeA - timeB;
            }
        },
        {
            title: t('patientName'),
            dataIndex: ['patientId', 'name'],
            key: 'patientName',
            render: (name, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{name || 'N/A'}</div>
                    {record.patientId && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.patientId.age || 'N/A'} yrs, {record.patientId.gender || 'N/A'}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: t('phone'),
            dataIndex: ['patientId', 'phoneNumber'],
            key: 'phone'
        },
        // A branch secretary's list covers several doctors, so it has to say which.
        ...(bookableDoctors.length > 1 ? [{
            title: t('doctor') || 'Doctor',
            dataIndex: ['doctorId', 'name'],
            key: 'doctor',
            render: (name) => name || '-'
        }] : []),
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status),
            filters: [
                { text: t('scheduled'), value: 'scheduled' },
                { text: t('arrived'), value: 'arrived' },
                { text: t('inConsultation'), value: 'in-consultation' },
                { text: t('completed'), value: 'completed' }
            ],
            onFilter: (value, record) => record.status === value
        },
        {
            title: t('waiting'),
            key: 'waiting',
            render: (_, record) => getWaitingTime(record.checkInTime)
        },
        {
            title: t('notes'),
            dataIndex: 'notes',
            key: 'notes',
            ellipsis: true,
            width: 150
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 200,
            fixed: 'right',
            render: (_, record) => {
                const getActionButtons = () => {
                    const buttons = [];

                    // View button - always available
                    buttons.push({
                        key: 'view',
                        label: (
                            <Button
                                type="link"
                                icon={<EyeOutlined />}
                                onClick={() => navigate(`${basePath}/patient/${record.patientId._id}`)}
                                block
                            >
                                {t('view')}
                            </Button>
                        )
                    });

                    // Scheduled status actions
                    if (record.status === 'scheduled') {
                        buttons.push({
                            key: 'checkIn',
                            label: (
                                <Button
                                    type="link"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => handleCheckIn(record._id)}
                                    block
                                >
                                    {t('checkIn')}
                                </Button>
                            )
                        });
                        buttons.push({
                            key: 'cancel',
                            label: (
                                <Popconfirm
                                    title={t('cancelThisAppointment')}
                                    onConfirm={() => handleCancel(record._id)}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                >
                                    <Button
                                        type="link"
                                        danger
                                        icon={<CloseCircleOutlined />}
                                        block
                                    >
                                        {t('cancel')}
                                    </Button>
                                </Popconfirm>
                            )
                        });
                    }

                    // Arrived status actions
                    if (record.status === 'arrived') {
                        if (!isDoctor) {
                            buttons.push({
                                key: 'notify',
                                label: (
                                    <Button
                                        type="link"
                                        icon={<SyncOutlined />}
                                        onClick={() => handleNotifyDoctor(record._id, record.patientId._id)}
                                        block
                                    >
                                        {t('notifyDoctor')}
                                    </Button>
                                )
                            });
                        } else {
                            buttons.push({
                                key: 'start',
                                label: (
                                    <Button
                                        type="link"
                                        icon={<SyncOutlined />}
                                        onClick={() => navigate(`${basePath}/patient/${record.patientId._id}`)}
                                        block
                                    >
                                        {t('startConsultation')}
                                    </Button>
                                )
                            });
                            buttons.push({
                                key: 'markDone',
                                label: (
                                    <Button
                                        type="link"
                                        icon={<CheckOutlined />}
                                        onClick={() => handleMarkCompleted(record._id)}
                                        block
                                        style={{ color: '#52c41a' }}
                                    >
                                        {t('markAsDone')}
                                    </Button>
                                )
                            });
                        }
                        buttons.push({
                            key: 'cancel',
                            label: (
                                <Popconfirm
                                    title={t('cancelThisAppointment')}
                                    onConfirm={() => handleCancel(record._id)}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                >
                                    <Button
                                        type="link"
                                        danger
                                        icon={<CloseCircleOutlined />}
                                        block
                                    >
                                        {t('cancel')}
                                    </Button>
                                </Popconfirm>
                            )
                        });
                    }

                    // In-consultation status actions
                    if (record.status === 'in-consultation') {
                        if (isDoctor) {
                            buttons.push({
                                key: 'complete',
                                label: (
                                    <Button
                                        type="link"
                                        icon={<CheckOutlined />}
                                        onClick={() => handleMarkCompleted(record._id)}
                                        block
                                        style={{ color: '#52c41a' }}
                                    >
                                        {t('sawPatient')}
                                    </Button>
                                )
                            });
                            buttons.push({
                                key: 'noShow',
                                label: (
                                    <Button
                                        type="link"
                                        danger
                                        icon={<StopOutlined />}
                                        onClick={() => handleMarkNoShow(record._id)}
                                        block
                                    >
                                        {t('noShow')}
                                    </Button>
                                )
                            });
                        } else {
                            buttons.push({
                                key: 'continue',
                                label: (
                                    <Button
                                        type="link"
                                        icon={<SyncOutlined />}
                                        onClick={() => navigate(`${basePath}/patient/${record.patientId._id}`)}
                                        block
                                    >
                                        {t('continue')}
                                    </Button>
                                )
                            });
                        }
                    }

                    return buttons;
                };

                const menuItems = getActionButtons();

                return (
                    <Dropdown
                        menu={{ items: menuItems }}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <Button type="primary" icon={<MoreOutlined />}>
                            {t('actions')}
                        </Button>
                    </Dropdown>
                );
            }
        }
    ];

    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 8; hour <= 17; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = dayjs().hour(hour).minute(minute);
                slots.push(time);
            }
        }
        return slots;
    };

    return (
        <div className="page-container">
            <Row gutter={[16, 16]} align="middle" justify="space-between" className="page-header">
                <Col xs={24} sm={16} md={12}>
                    <h1 className="page-title">
                        📅 {t('todaysAppointments')}
                        <br />{dayjs().format('DD-MM-YYYY')}
                    </h1>
                </Col>
                <Col xs={24} sm={8} md={12} style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleBookAppointment}
                        size="large"
                    >
                        {t('bookAppointment')}
                    </Button>
                </Col>
            </Row>

            <Card className="search-filter-card">
                <SearchFilter
                    searchFields={[
                        { key: 'patientName', label: t('patientName'), placeholder: t('searchBy') + ' ' + t('patientName') },
                        { key: 'phoneNumber', label: t('phoneNumber'), placeholder: t('searchBy') + ' ' + t('phoneNumber') }
                    ]}
                    filterFields={[
                        {
                            key: 'status',
                            label: t('status'),
                            options: [
                                { label: t('scheduled'), value: 'scheduled' },
                                { label: t('arrived'), value: 'arrived' },
                                { label: t('inConsultation'), value: 'in-consultation' },
                                { label: t('completed'), value: 'completed' },
                                { label: t('cancelled'), value: 'cancelled' }
                            ]
                        }
                    ]}
                    onSearch={handleSearch}
                    onReset={handleReset}
                />
            </Card>

            <Card className="table-card">
                <Table
                    columns={columns}
                    dataSource={filteredAppointments}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 1000 }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${t('total')}: ${total}`,
                    }}
                    rowClassName={(record) => {
                        if (record.status === 'arrived') return 'arrived-row';
                        if (record.status === 'in-consultation') return 'consultation-row';
                        return '';
                    }}
                />
            </Card>

            <FormModal
                title={t('bookAppointment')}
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setIsNewPatient(false);
                }}
                onSubmit={handleSubmit}
                form={form}
                width={750}
                className="appointment-form-modal"
            >
                {/* Patient Type Section */}
                <div className="appointment-form-section">
                    <label className="section-title">{t('patientType')}</label>
                    <Radio.Group onChange={handlePatientTypeChange} defaultValue="existing" style={{ width: '100%', display: 'flex' }}>
                        <Radio.Button value="existing" style={{ flex: 1, textAlign: 'center', padding: '0px 0' }}>
                            {t('existingPatient')}
                        </Radio.Button>
                        <Radio.Button value="new" style={{ flex: 1, textAlign: 'center', padding: '0px 0' }}>
                            <UserAddOutlined style={{ marginRight: '6px' }} />
                            {t('newPatient')}
                        </Radio.Button>
                    </Radio.Group>
                </div>

                {/* Which doctor - only when this account serves more than one. */}
                {bookableDoctors.length > 1 && (
                    <div className="appointment-form-section">
                        <label className="section-title">{t('doctor') || 'Doctor'}</label>
                        <Form.Item
                            name="doctorId"
                            rules={[{ required: true, message: (t('doctor') || 'Doctor') + ' ' + t('required') }]}
                            style={{ marginBottom: 0 }}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                placeholder={t('selectDoctor') || 'Select the doctor'}
                                size="large"
                                options={bookableDoctors.map((d) => ({
                                    label: d.specialization ? `${d.name} — ${d.specialization}` : d.name,
                                    value: d._id
                                }))}
                            />
                        </Form.Item>
                    </div>
                )}

                {/* Patient Information Section */}
                <div className="appointment-form-section">
                    <label className="section-title">{t('patientInformation')}</label>

                    {isNewPatient && duplicates.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={t('patientAlreadyExists') || 'This patient already exists'}
                            description={
                                <Space direction="vertical" size={4}>
                                    {duplicates.map((p) => (
                                        <Button
                                            key={p._id}
                                            type="link"
                                            style={{ padding: 0, height: 'auto' }}
                                            onClick={() => selectExistingPatient(p)}
                                        >
                                            {p.name} — {p.phoneNumber} ({t('useThisPatient') || 'use this patient'})
                                        </Button>
                                    ))}
                                </Space>
                            }
                        />
                    )}

                    {!isNewPatient ? (
                        <Form.Item
                            name="patientId"
                            rules={[{ required: !isNewPatient, message: t('selectPatient') + ' ' + t('required') }]}
                            style={{ marginBottom: 0 }}
                        >
                            <Select
                                showSearch
                                placeholder={patients.length > 0 ? t('searchAndSelectPatient') : t('noPatients')}
                                notFoundContent={patients.length === 0 ? t('noPatientsFound') : t('noMatchFound')}
                                filterOption={(input, option) => {
                                    const patient = patients.find(p => p._id === option?.value);
                                    if (!patient) return false;

                                    const searchTerm = input.toLowerCase();
                                    const matchesName = patient.name.toLowerCase().includes(searchTerm);
                                    const matchesPhone = patient.phoneNumber.includes(searchTerm);

                                    return matchesName || matchesPhone;
                                }}
                                options={patients.map(patient => ({
                                    label: `${patient.name} - ${patient.phoneNumber}${patient.age ? ` (${patient.age} ${t('years')}, ${t(patient.gender)})` : ''}`,
                                    value: patient._id
                                }))}
                                size="large"
                                loading={patients.length === 0}
                            />
                        </Form.Item>
                    ) : (
                            <div>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={24} md={24}>
                                        <Form.Item
                                            name="name"
                                            rules={[{ required: true, message: t('patientName') + ' ' + t('required') }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder={t('enterFullName')} size="large" onBlur={lookForDuplicates} />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item
                                            name="phoneNumber"
                                            rules={[{ required: true, message: t('phoneNumber') + ' ' + t('required') }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder={t('enterPhoneNumber')} size="large" onBlur={lookForDuplicates} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item
                                            name="age"
                                            rules={[{ required: true, message: t('age') + ' ' + t('required') }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input type="number" placeholder={t('enterAge')} size="large" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item
                                            name="gender"
                                            rules={[{ required: true, message: t('gender') + ' ' + t('required') }]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select placeholder={t('select')} size="large">
                                                <Select.Option value="male">{t('male')}</Select.Option>
                                                <Select.Option value="female">{t('female')}</Select.Option>
                                                <Select.Option value="other">{t('other')}</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                                    <Col xs={24}>
                                        <Form.Item name="address" style={{ marginBottom: 0 }}>
                                            <Input.TextArea placeholder={t('enterAddress')} rows={2} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                    )}
                </div>

                {/* Appointment Details Section */}
                <div className="appointment-form-section">
                    <label className="section-title">{t('appointmentDetails')}</label>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={12}>
                            <Form.Item
                                name="appointmentDate"
                                rules={[{ required: true, message: t('appointmentDate') + ' ' + t('required') }]}
                                style={{ marginBottom: 0 }}
                            >
                                <DatePicker style={{ width: '100%' }} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={12}>
                            <Form.Item
                                name="appointmentTime"
                                rules={[{ required: true, message: t('appointmentTime') + ' ' + t('required') }]}
                                style={{ marginBottom: 0 }}
                            >
                                <TimePicker format="hh:mm A" use12Hours style={{ width: '100%' }} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
                        <Col xs={24} sm={12} md={12}>
                            <Form.Item
                                name="paymentAmount"
                                label={t('paymentAmount')}
                                rules={[{ required: true, message: t('paymentAmount') + ' ' + t('required') }]}
                                style={{ marginBottom: 0 }}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    size="large"
                                    min={0}
                                    placeholder={t('enterPaymentAmount')}
                                    addonAfter="IQD"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={12}>
                            <Form.Item name="notes" label={t('notes')} style={{ marginBottom: 0 }}>
                                <Input.TextArea placeholder={t('enterNotes')} rows={2} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </FormModal>

            <DuplicatePatientModal
                open={duplicateModalOpen}
                matches={duplicates}
                creating={booking}
                onSelect={selectExistingPatient}
                onCreateAnyway={() => pendingBooking && submitBooking(pendingBooking, { force: true })}
                onCancel={() => setDuplicateModalOpen(false)}
            />
        </div>
    );
};

export default Appointments;
